import {
  CONSENT_DATALAYER_EVENT,
  ConsentDataLayerPayload,
  ConsentRegime,
  ConsentState,
  googleConsentPayload,
} from "@shared/consent";

/**
 * The bridge from our consent state into Google Tag Manager.
 *
 * Two signals go out on every change, because the container holds two kinds of
 * tag:
 *
 *  1. `gtag('consent', 'update', …)` — Google's own tags (GA4, Google Ads)
 *     read this natively and adjust what they store and send. Nothing has to be
 *     configured in the container for it to work.
 *
 *  2. a `cookie_consent_update` dataLayer event carrying plain booleans — Meta,
 *     TikTok and any other third-party tag ignore Consent Mode entirely, so
 *     they are blocked in the container until this event says otherwise.
 *     docs/CONSENT.md has the exact trigger and variable setup.
 *
 * Sending only the first is the classic way to end up "compliant" on paper
 * while the Meta pixel keeps firing.
 */

type DataLayerWindow = Window & {
  dataLayer?: unknown[];
  __ocConsentBootstrapped?: boolean;
};

function dataLayer(): unknown[] {
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
}

/**
 * Consent commands have to reach the dataLayer as an Arguments object; that is
 * how GTM distinguishes them from ordinary event pushes. Pushing an array here
 * would leave the command silently ignored.
 *
 * Deliberately not exposed as `window.gtag`. Defining that global would turn
 * every `gtagEvent()` call in lib/tracking/channels.ts from a no-op into a live
 * push and duplicate events across the container.
 */
const gtagConsent: (...args: unknown[]) => void = function () {
  // eslint-disable-next-line prefer-rest-params
  dataLayer().push(arguments);
};

/**
 * True when the inline snippet in index.html already applied a stored choice
 * before GTM loaded. The provider uses it to avoid pushing an identical state a
 * second time, which would re-fire every container trigger listening for the
 * consent event.
 */
export function consentBootstrappedInHtml(): boolean {
  return !!(window as DataLayerWindow).__ocConsentBootstrapped;
}

export function pushConsentToGTM(
  state: ConsentState,
  regime: ConsentRegime,
  country: string | null,
  source: ConsentDataLayerPayload["consent_source"]
): void {
  gtagConsent("consent", "update", googleConsentPayload(state));

  const payload: ConsentDataLayerPayload = {
    event: CONSENT_DATALAYER_EVENT,
    consent_functional: state.functional,
    consent_analytics: state.analytics,
    consent_marketing: state.marketing,
    consent_regime: regime,
    consent_country: country,
    consent_source: source,
  };
  dataLayer().push(payload);
}
